import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import {
  Users, Plus, Loader2, Trash2, Pencil, ShieldCheck,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", role: "coordinator", sector_id: "" });
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [u, s] = await Promise.all([api.get("/users"), api.get("/sectors")]);
      setUsers(u.data);
      setSectors(s.data);
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  function openNew() {
    setEditing(null);
    setForm({ email: "", name: "", role: "coordinator", sector_id: "" });
    setOpen(true);
  }

  function openEdit(u) {
    setEditing(u);
    setForm({
      email: u.email,
      name: u.name || "",
      role: u.role,
      sector_id: u.sector_id || "",
    });
    setOpen(true);
  }

  async function save() {
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.role !== "coordinator") payload.sector_id = null;
      if (editing) {
        await api.put(`/users/${editing.id}`, {
          name: payload.name,
          role: payload.role,
          sector_id: payload.sector_id || null,
        });
        toast.success("Usuário atualizado");
      } else {
        await api.post("/users", payload);
        toast.success("Usuário criado");
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally { setSaving(false); }
  }

  async function confirmDelete() {
    try {
      await api.delete(`/users/${deleteId}`);
      toast.success("Usuário excluído");
      setDeleteId(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao excluir");
    }
  }

  const sectorMap = Object.fromEntries(sectors.map((s) => [s.id, s.name]));

  return (
    <div>
      <PageHeader
        title="Usuários"
        subtitle="Gerencie Super Administradores e Coordenadores. Coordenadores só enxergam seu próprio setor."
        action={
          <Button data-testid="new-user-btn" onClick={openNew} className="bg-[#1e5a8e] hover:bg-[#164570]">
            <Plus className="w-4 h-4 mr-2" /> Novo usuário
          </Button>
        }
      />

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : users.length === 0 ? (
        <EmptyState icon={Users} title="Nenhum usuário" description="Adicione administradores e coordenadores para gerenciar a plataforma." />
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-left">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Nome</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">E-mail</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Papel</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Setor</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.email}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50">
                  <td className="px-6 py-4 text-sm text-slate-900 font-medium">{u.name || "—"}</td>
                  <td className="px-6 py-4 text-sm text-slate-600">{u.email}</td>
                  <td className="px-6 py-4 text-sm">
                    {u.role === "super_admin" ? (
                      <Badge className="bg-[#1e5a8e] hover:bg-[#1e5a8e] text-white gap-1">
                        <ShieldCheck className="w-3 h-3" /> Super Admin
                      </Badge>
                    ) : (
                      <Badge variant="outline">Coordenador</Badge>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">{u.sector_id ? sectorMap[u.sector_id] || "—" : "—"}</td>
                  <td className="px-6 py-4 text-right whitespace-nowrap">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(u)} data-testid={`edit-user-${u.email}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-600" onClick={() => setDeleteId(u.id)} data-testid={`delete-user-${u.email}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="user-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar usuário" : "Novo usuário"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>E-mail *</Label>
              <Input data-testid="user-email" type="email" value={form.email} disabled={!!editing} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="usuario@ifpr.edu.br" />
            </div>
            <div>
              <Label>Nome</Label>
              <Input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label>Papel *</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="super_admin">Super Administrador</SelectItem>
                  <SelectItem value="coordinator">Coordenador</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.role === "coordinator" && (
              <div>
                <Label>Setor *</Label>
                <Select value={form.sector_id || ""} onValueChange={(v) => setForm({ ...form, sector_id: v })}>
                  <SelectTrigger data-testid="user-sector"><SelectValue placeholder="Selecione o setor" /></SelectTrigger>
                  <SelectContent>
                    {sectors.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button data-testid="save-user-btn" onClick={save} disabled={saving || !form.email || (form.role === "coordinator" && !form.sector_id)} className="bg-[#1e5a8e] hover:bg-[#164570]">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir usuário?</AlertDialogTitle>
            <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
